import { Experience } from "types/experience";
import { WebRule } from "types/web-rule";

/**
 * Availability states for a section, covering both regular seats and waitlist.
 *
 * Regular seats:
 *   Available                    — has regular seats for this group size
 *   UnavailableForGroupSize      — has regular seats, but not for this group size; no waitlist for this group
 *
 * Waitlist seats (no regular seats available):
 *   WaitListAvailable            — no regular seats, but waitlist is open for this group size
 *   WaitListUnavailableForGroupSize — waitlist exists, but not for this group size
 *
 * Nothing available:
 *   FullyBooked                  — no regular seats and no waitlist at all
 *   TooLargeGroup                — group size exceeds the maximum defined by web rules
 */
export enum AvailabilityState {
    Available = "available",
    WaitListAvailable = "waitListAvailable",
    UnavailableForGroupSize = "unavailableForGroupSize",
    WaitListUnavailableForGroupSize = "waitListUnavailableForGroupSize",
    FullyBooked = "fullyBooked",
    TooLargeGroup = "tooLargeGroup",
}

/**
 * Returns true for states where the section has something to show the current group
 * (either regular times or waitlist times). Used to filter sections in the time picker
 * and to decide whether to show the "unavailable" badge in the activity picker.
 */
export function isSectionShowable(state: AvailabilityState): boolean {
    return state === AvailabilityState.Available || state === AvailabilityState.WaitListAvailable;
}

/**
 * Determines the availability state of a section for a given group size,
 * considering both regular seats and waitlist seats.
 */
export const getAvailabilityState = (timeSets: WebRule[], guests: number): AvailabilityState => {
    if (timeSets.length === 0) {
        return AvailabilityState.FullyBooked;
    }

    const exceedsMaxGroupSize = timeSets.every(
        (timeSet) => timeSet.maxGroupSize > 0 && guests > timeSet.maxGroupSize
    );

    if (exceedsMaxGroupSize) {
        return AvailabilityState.TooLargeGroup;
    }

    const activeTimeSlots = timeSets
        .flatMap((timeSet) => timeSet.times)
        .filter((time) => !time.timeExpired);

    // Regular seats for this group
    if (activeTimeSlots.some((time) => time.availableSeats?.includes(guests))) {
        return AvailabilityState.Available;
    }

    // Waitlist for this group
    if (activeTimeSlots.some((time) => time.waitListSeats?.includes(guests))) {
        return AvailabilityState.WaitListAvailable;
    }

    // Regular seats exist, but not for this group size
    if (activeTimeSlots.some((time) => time.availableSeats && time.availableSeats.length > 0)) {
        return AvailabilityState.UnavailableForGroupSize;
    }

    // Waitlist exists, but not for this group size
    if (activeTimeSlots.some((time) => time.waitListSeats && time.waitListSeats.length > 0)) {
        return AvailabilityState.WaitListUnavailableForGroupSize;
    }

    return AvailabilityState.FullyBooked;
};

/**
 * Text rows carry a message instead of times, so a set of nothing else can never be fully booked.
 * Rules closed on the day don't count, as in the API's day states.
 */
export const isTextRowsOnly = (timeSets: WebRule[]): boolean => {
    const open = timeSets.filter((rule) => !rule.isClosed);
    return open.length > 0 && open.every((rule) => rule.isTextRow);
};

/**
 * Null when nothing should be surfaced: without a party size every seat check reads as fully booked,
 * booking requests are selectable regardless of seats, and all-addition rules carry a message
 * instead of times (BOOK-3814). Recomputed because the modal builds its own sections and leaves
 * `availabilityState` unset.
 */
export const getAvailabilityStateForBadge = (
    timeSets: WebRule[],
    guests: number | null | undefined,
    isBookingRequest: boolean
): AvailabilityState | null => {
    if (!guests || isBookingRequest) return null;
    if (isTextRowsOnly(timeSets)) return null;

    return getAvailabilityState(timeSets, guests);
};

/** Null for chained experiences: availability depends on whole chains, and they take no waitlist. */
export const getExperienceAvailabilityState = (
    experience: Experience,
    guests: number | null | undefined,
    isBookingRequest: boolean
): AvailabilityState | null => {
    if (experience.activities.length !== 1) return null;

    return getAvailabilityStateForBadge(
        experience.activities[0].timeSets,
        guests,
        isBookingRequest || experience.bookingRequestOnly
    );
};

/** Whether AvailabilityBadge would render for this state, for callers deciding on a surrounding row. */
export const hasAvailabilityBadge = (state: AvailabilityState | null | undefined): boolean =>
    getAvailabilityBadgeKey(state ?? null) !== null;

export const getAvailabilityBadgeKey = (state: AvailabilityState | null): string | null => {
    switch (state) {
        case AvailabilityState.WaitListAvailable:
            return "availability.waitListOnly";
        case AvailabilityState.FullyBooked:
        case AvailabilityState.WaitListUnavailableForGroupSize:
            return "fullyBooked";
        case AvailabilityState.UnavailableForGroupSize:
        case AvailabilityState.TooLargeGroup:
            return "limitedAvailability";
        default:
            return null;
    }
};
